import { useState } from 'react';
import Image from 'next/image';

import {
  Box,
  Card,
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  List,
  ListIcon,
  ListItem,
  RadioProps,
  Skeleton,
  Tag,
  TagLabel,
  Text,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { trackEvent } from '~/features/analytics/track-event';
import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { CouponAppliedTag } from '~/features/experiments/ab_plp_redesign_2025/components/coupon-applied-tag';
import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import {
  AwardIcon,
  CheckIcon,
  CircleArrowRightIcon,
  DiscountIcon,
} from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';

import { shouldSeeAnnualBundlePlan } from '../../checkout/utils/should-see-annual-plan';

export type ExperiencePlansSectionProps = StaticExperiencePlansSectionProps;
export function ExperiencePlansSection({
  title,
  subtitle,
  description,
  features,
  subsectionTitle,
  subsectionFeatures,
}: ExperiencePlansSectionProps) {
  const { currency } = useCurrency();
  const { data: plans } = usePricePlans(currency);
  const { data: promotionData } = usePromotion();
  const isBundleAnnualPlan = shouldSeeAnnualBundlePlan(currency);
  const defaultSelectedPlan =
    promotionData?.defaultSelectedPlan === billingFrequencySchema.enum['4month'] && isBundleAnnualPlan
      ? billingFrequencySchema.enum['annually']
      : promotionData?.defaultSelectedPlan ?? billingFrequencySchema.enum.monthly;
  const bundlePlan = plans?.prices[isBundleAnnualPlan ? 'annually' : '4month']?.individual;
  return (
    <Flex
      as='section'
      bgColor='grey.50'
      flexDirection='column'
      gap='56px'
      id='plans'
      padding={{
        base: '56px 0px',
        md: '112px 0px',
      }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md' display='flex' flexDirection='column' gap='56px'>
        <SectionHeader
          color='grey.500'
          iconSlot={<AwardIcon color='grey.500' height='18px' width='18px' />}
          subtitle={subtitle}
          descriptionSlot={
            <Text color='grey.500' margin='0 auto' maxWidth='886px'>
              {description}
            </Text>
          }
          titleSlot={
            <Heading
              as='h2'
              fontWeight='600'
              lineHeight='1.1'
              margin='0 auto'
              maxWidth='886px'
              width='100%'
              fontSize={{
                base: '2.25rem',
                md: '3.375rem',
              }}
            >
              {title}
            </Heading>
          }
        />
        <List
          display='flex'
          gap={4}
          justifyContent='center'
          flexDirection={{
            base: 'column',
            md: 'row',
          }}
        >
          {features.map((feature) => (
            <ListItem key={feature.title} display='flex' flex='1'>
              <Card
                as='article'
                bgColor='white'
                border='none'
                borderRadius='8px'
                boxShadow='none'
                width='100%'
                gap={{
                  base: 4,
                  md: 6,
                }}
                p={{
                  base: '20px',
                  md: '28px',
                }}
              >
                <Flex
                  gap={4}
                  alignItems={{
                    base: 'center',
                    md: 'flex-start',
                  }}
                  flexDirection={{
                    md: 'column',
                  }}
                >
                  <Box
                    flex={{
                      base: '0 0 42px',
                      md: '0 0 48px',
                    }}
                  >
                    <Image alt='' height={48} src={feature.iconPath} width={48} />
                  </Box>
                  <Heading
                    as='h3'
                    fontSize='1.25rem'
                    fontWeight='600'
                    lineHeight='1.2'
                    whiteSpace={{
                      md: 'pre-wrap',
                    }}
                  >
                    {feature.title}
                  </Heading>
                </Flex>
                <Text color='grey.500'>{feature.description}</Text>
              </Card>
            </ListItem>
          ))}
        </List>

        <Flex flexDirection='column' gap={6}>
          <Card
            bgGradient='linear(98.24deg, #00298C 0%, #000B26 100%)'
            boxShadow='none'
            color='white'
            overflow='hidden'
            position='relative'
            borderRadius={{
              base: '0px',
              md: '8px',
            }}
            mx={{
              base: '-16px',
              md: '0px',
            }}
            padding={{
              base: '40px 16px',
              md: '48px 32px 48px 72px',
            }}
          >
            <Box
              bottom={0}
              pointerEvents='none'
              position='absolute'
              right={0}
              display={{
                base: 'none',
                md: 'block',
              }}
            >
              <Image
                alt=''
                height={720}
                loading='lazy'
                quality={75}
                src='/images/experiments/ab_plp_redesign_2025/udacity-u-bg.png'
                width={530}
              />
            </Box>
            <Flex
              justifyContent='space-between'
              position='relative'
              alignItems={{
                md: 'center',
              }}
              flexDirection={{
                base: 'column',
                md: 'row',
              }}
              gap={{
                base: 10,
                md: 12,
              }}
            >
              <Flex
                flexDirection='column'
                gap={8}
                flex={{
                  md: '0 1 466px',
                }}
              >
                <Heading
                  as='h3'
                  fontWeight='600'
                  fontSize={{
                    base: '1.75rem',
                    md: '2.25rem',
                  }}
                  lineHeight={{
                    base: '1.14',
                    md: '1.2',
                  }}
                >
                  {subsectionTitle}
                </Heading>
                <List display='flex' flexDirection='column' gap={3}>
                  {subsectionFeatures.map((feature) => (
                    <ListItem key={feature} display='flex'>
                      <ListIcon as={CheckIcon} color='cyan.500' height='27px' width='18px' />
                      <Markdown
                        markdownText={feature}
                        components={{
                          strong: ({ children }) => (
                            <Text
                              as='strong'
                              fontWeight='600'
                              fontSize={{
                                base: '1rem',
                                md: '1.125rem',
                              }}
                              lineHeight={{
                                base: '1.4',
                                md: '1.5',
                              }}
                            >
                              {children}
                            </Text>
                          ),
                          p: ({ children }) => (
                            <Text
                              as='p'
                              fontWeight='400'
                              fontSize={{
                                base: '1rem',
                                md: '1.125rem',
                              }}
                              lineHeight={{
                                base: '1.4',
                                md: '1.5',
                              }}
                            >
                              {children}
                            </Text>
                          ),
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Flex>
              <Flex
                flexDirection='column'
                gap={4}
                flex={{
                  base: '1',
                  md: '0 0 360px',
                }}
              >
                <Flex bgColor='grey.900' borderRadius='8px' flexDirection='column' padding='8px'>
                  <PlanRadioGroup
                    key={defaultSelectedPlan}
                    defaultSelectedPlan={defaultSelectedPlan}
                    renderButton={({ selectedPlan }) => (
                      <ButtonLink
                        buttonProps={{
                          variant: 'solid',
                          colorScheme: 'blue',
                          size: 'icon-left',
                          leftIcon: <CircleArrowRightIcon color='white' height='18px' width='18px' />,
                          onClick: () => {
                            trackEvent({
                              name: 'ExperiencePage CTA Clicked',
                              type: 'click',
                              label: `${selectedPlan}_enroll_now`,
                            });
                          },
                        }}
                        linkProps={{
                          href: `/checkout/individual/${selectedPlan}/credit-card`,
                        }}
                      >
                        Subscribe Now
                      </ButtonLink>
                    )}
                  />
                </Flex>
                <PromotionBanner />
              </Flex>
            </Flex>
          </Card>
          {bundlePlan && (
            <Text alignSelf='center' color='grey.500' fontSize='xs'>
              <Box as='sup'>&#42;</Box>
              {isBundleAnnualPlan
                ? `Discount applies to the first year of membership, after which plans are converted to month-to-month. Total price of ${bundlePlan.displayPrice} for an annual subscription.`
                : `Discount applies to the first 4 months of membership, after which plans are converted to month-to-month. Total price of ${bundlePlan.displayPrice} for a 4-month bundle, based on the average time to complete a Nanodegree program.`}
            </Text>
          )}
        </Flex>
      </ContentContainer>
    </Flex>
  );
}

function PlanRadioGroup({
  defaultSelectedPlan,
  renderButton,
}: {
  defaultSelectedPlan?: BillingFrequency;
  renderButton: ({ selectedPlan }: { selectedPlan: BillingFrequency }) => React.ReactNode;
}) {
  const [selectedPlan, setSelectedPlan] = useState<BillingFrequency>(
    defaultSelectedPlan || billingFrequencySchema.enum.monthly
  );
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'udacity-all-access-plans',
    defaultValue: defaultSelectedPlan || billingFrequencySchema.enum.monthly,
    onChange: (plan) => setSelectedPlan(plan as BillingFrequency),
    value: selectedPlan,
  });

  const group = getRootProps();

  const { currency } = useCurrency();
  const { data: plans } = usePricePlans(currency);

  const monthlyPlan = plans?.prices[billingFrequencySchema.enum.monthly]?.individual;
  const isBundleAnnualPlan = shouldSeeAnnualBundlePlan(currency);
  const bundlePlan = plans?.prices[isBundleAnnualPlan ? 'annually' : '4month']?.individual;

  const ALL_ACCESS_PLAN_ITEMS = [
    {
      label: 'Monthly Subscription',
      description: 'Unlimited access to 500+ courses',
      value: billingFrequencySchema.enum.monthly,
      ...monthlyPlan,
      displayPrice: monthlyPlan?.displayPricePerMonth,
      displayPriceUnits: ' / month',
    },
    {
      label: 'Bundle Subscription',
      description: `${bundlePlan?.displayPrice} for ${isBundleAnnualPlan ? 'an annual' : 'a 4-month'} bundle`,
      value: isBundleAnnualPlan ? billingFrequencySchema.enum['annually'] : billingFrequencySchema.enum['4month'],
      ...bundlePlan,
      displayPrice: bundlePlan?.displayPricePerMonth,
      displayPriceUnits: ' / month',
      strikeThroughDisplayPrice: bundlePlan?.strikeThroughDisplayPricePerMonth,
      savingsBlurb: isBundleAnnualPlan ? '20% off' : '15% off',
    },
  ];

  function renderPrice({
    displayPrice,
    displayPriceUnits,
    strikeThroughDisplayPrice,
    couponApplied,
    couponPercentOff,
  }: {
    displayPrice: string | undefined;
    displayPriceUnits: string | undefined;
    strikeThroughDisplayPrice: string | null | undefined;
    couponApplied: boolean | undefined;
    couponPercentOff: number | null | undefined;
  }) {
    if (!displayPrice)
      return (
        <Flex alignItems='center' flexDirection='row' gap={2}>
          <Skeleton height='28px' width='92px' />
          <Skeleton height='18px' width='47px' />
        </Flex>
      );
    else if (strikeThroughDisplayPrice) {
      return (
        <>
          <Flex alignItems='center' gap={2}>
            <Heading alignItems='center' as='p' display='flex' flexWrap='wrap' gap='4px'>
              <Heading
                as='del'
                color='grey.500'
                fontFamily='body'
                fontSize='2.25rem'
                fontWeight='600'
                lineHeight='1.75rem'
                position='relative'
                textDecor='line-through'
                textDecorationThickness='2px'
              >
                {strikeThroughDisplayPrice}
              </Heading>
            </Heading>
            {couponApplied && typeof couponPercentOff === 'number' && <CouponAppliedTag value={couponPercentOff} />}
          </Flex>
          <Heading
            alignItems='center'
            as='ins'
            color='supportGray.100'
            display='flex'
            flexWrap='wrap'
            fontFamily='body'
            fontSize='2.25rem'
            fontWeight='600'
            gap={2}
            lineHeight='1.75rem'
            textDecor='none'
          >
            {displayPrice}{' '}
            <Heading
              as='span'
              color='grey.500'
              fontFamily='body'
              fontSize='0.8125rem'
              fontWeight='400'
              lineHeight='1.4'
            >
              {displayPriceUnits}
            </Heading>
          </Heading>
        </>
      );
    } else {
      return (
        <Flex flexWrap='wrap' gap={3}>
          <Heading
            alignItems='center'
            as='p'
            color='grey.900'
            display='flex'
            fontFamily='body'
            fontSize='2.25rem'
            fontWeight='600'
            gap={2}
            lineHeight='1.75rem'
          >
            {displayPrice}{' '}
            <Text as='span' color='grey.500' fontSize='0.8125rem' fontWeight='400' lineHeight='1.4'>
              {displayPriceUnits}
            </Text>
          </Heading>
        </Flex>
      );
    }
  }

  return (
    <Flex flexDirection='column' gap={2} height='100%' {...group}>
      {ALL_ACCESS_PLAN_ITEMS.map((plan) => {
        const radio = getRadioProps({ value: plan.value });
        const savingsBlurb = 'savingsBlurb' in plan ? plan.savingsBlurb : undefined;
        const isSavingsBlurbRendered = !!savingsBlurb && !plan.couponApplied;

        return (
          <PlanRadioCard key={plan.value} {...radio} ariaLabel={plan.label}>
            <Box marginTop='4px'>
              {radio.isChecked ? (
                <svg fill='none' height='20' viewBox='0 0 20 20' width='20' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10Z'
                    fill='#175CFF'
                  />
                  <path
                    d='M10 15C12.7614 15 15 12.7614 15 10C15 7.23858 12.7614 5 10 5C7.23858 5 5 7.23858 5 10C5 12.7614 7.23858 15 10 15Z'
                    fill='white'
                  />
                </svg>
              ) : (
                <svg fill='none' height='20' viewBox='0 0 20 20' width='20' xmlns='http://www.w3.org/2000/svg'>
                  <rect fill='#F2F2F2' height='18.67' rx='9.335' width='18.67' x='0.665' y='0.665' />
                  <rect
                    height='18.67'
                    rx='9.335'
                    stroke='#9C9FA8'
                    strokeWidth='1.33'
                    width='18.67'
                    x='0.665'
                    y='0.665'
                  />
                </svg>
              )}
            </Box>
            <Flex
              flexDirection='column'
              gap={3}
              {...(isSavingsBlurbRendered && {
                paddingRight: '60px',
              })}
              flex={1}
              position='relative'
            >
              {isSavingsBlurbRendered && (
                <Tag
                  bgGradient='linear(90deg, #931CF5 0%, #6100B0 100%)'
                  border='none'
                  borderBottomLeftRadius='4px'
                  borderBottomRightRadius='0'
                  borderTopLeftRadius='4px'
                  borderTopRightRadius='0'
                  padding='8px 12px'
                  position='absolute'
                  right='-21px'
                  top='0px'
                >
                  <TagLabel
                    color='white'
                    fontFamily='condensed'
                    fontSize='0.9375rem'
                    fontWeight='500'
                    letterSpacing='1px'
                    lineHeight='1'
                    textTransform='uppercase'
                  >
                    {savingsBlurb}{' '}
                    <Box as='sup' fontSize='xs' fontWeight='400'>
                      &#42;
                    </Box>
                  </TagLabel>
                </Tag>
              )}
              <Box>
                <Heading color='inherit' fontSize='1.25rem' fontWeight='600' lineHeight='1.2' mb='2px'>
                  {plan.label}
                </Heading>
                <Text color='grey.500' fontSize='0.8125rem' fontWeight='400' lineHeight='1.4'>
                  {plan.description}
                </Text>
              </Box>
              {renderPrice({
                displayPrice: plan.displayPrice,
                displayPriceUnits: plan.displayPriceUnits,
                strikeThroughDisplayPrice: plan.strikeThroughDisplayPrice,
                couponApplied: plan.couponApplied,
                couponPercentOff: plan.couponPercentOff,
              })}
            </Flex>
          </PlanRadioCard>
        );
      })}
      {renderButton({ selectedPlan })}
    </Flex>
  );
}

function PlanRadioCard({ ariaLabel, ...props }: RadioProps & { ariaLabel: string }) {
  const { getInputProps, getRadioProps } = useRadio(props);

  const input = getInputProps();
  const radio = getRadioProps();

  return (
    <Box as='label' flex={1} height='100%'>
      <input {...input} aria-label={ariaLabel} />
      <Box
        {...radio}
        alignItems='flex-start'
        bgColor='white'
        borderRadius='4px'
        boxShadow='none'
        color='grey.900'
        cursor='pointer'
        display='flex'
        gap={4}
        height='100%'
        padding='20px 20px 22px'
        _checked={{
          color: 'blue.500',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
      >
        {props.children}
      </Box>
    </Box>
  );
}

function PromotionBanner() {
  const { data: promotion } = usePromotion();

  function fireTrackEvent() {
    trackEvent({
      type: 'click',
      name: 'ExperiencePage CTA Clicked',
      label: 'hero_sticker_clicked',
      value: promotion?.promotionId,
    });
  }

  const heroSticker = promotion?.heroSticker;
  if (!heroSticker?.linkUrl || !heroSticker.text || !heroSticker.linkLabel) return null;

  return (
    <Box position='relative'>
      <LinkOverlay href={heroSticker.linkUrl} onClick={fireTrackEvent}>
        <LinkBox
          display='flex'
          fontSize='0.8125rem'
          fontWeight='400'
          gap={2}
          justifyContent='center'
          lineHeight='1.4'
          textAlign='center'
        >
          <DiscountIcon height='19px' width='20px' />
          <Box as='span' display='inline-flex' gap={1}>
            {heroSticker.text}
            <Box alignItems='center' as='span' color='cyan.300' display='flex' gap='4px' textDecoration='underline'>
              {heroSticker.linkLabel}
            </Box>
          </Box>
        </LinkBox>
      </LinkOverlay>
    </Box>
  );
}

export function transformStaticExperiencePlansSectionProps() {
  return {
    title: 'Unlock the learning that moves your career forward',
    subtitle: 'Udacity Subscription',
    description:
      "Access Udacity's catalog of 500+ courses with one subscription. Learn at your pace, in your own way, and master the skills that are in demand at today's top companies.",
    features: [
      {
        iconPath: '/images/experiments/ab_plp_redesign_2025/unlimited-icon.svg',
        title: 'Unlimited access to the catalog',
        description: 'Explore 500+ courses in AI, Data, Cloud, Programming, and more.',
      },
      {
        iconPath: '/images/experiments/ab_plp_redesign_2025/cancel-icon.svg',
        title: 'Cancel anytime,\nhassle-free',
        description:
          'Learn on your schedule with no long-term commitment. Pause or stop your studies whenever you need.',
      },
      {
        iconPath: '/images/experiments/ab_plp_redesign_2025/projects-icon.svg',
        title: 'Hands-on projects,\nexpert feedback',
        description:
          'Build real-world skills through projects reviewed by industry professionals, so you can deliver on the job from day one.',
      },
      {
        iconPath: '/images/experiments/ab_plp_redesign_2025/certificate-icon.svg',
        title: 'Certificates that prove your progress',
        description:
          'Earn a graduation certificate for every Nanodegree program you complete and showcase your skills to employers.',
      },
    ],
    subsectionTitle: 'The smartest investment for your career journey',
    subsectionFeatures: [
      '**Unlimited access** to hundreds of top-rated courses',
      '**Hands-on projects** with expert feedback',
      '**Personalized career coaching** and interview prep',
      '**Earn program certificates** to showcase your skills',
      '**Try risk-free** — cancel anytime',
    ],
  };
}

export type StaticExperiencePlansSectionProps = ReturnType<typeof transformStaticExperiencePlansSectionProps>;
